import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { subscribe } from 'lib/pusher';
import type {
  DpsScheduleCompleteEvent,
  DpsSlotsReadyEvent,
  DpsScheduleResponse,
  DpsScheduleStatus,
  DpsSnapshotStepEvent,
  SlotDateOverride,
} from './types';
import {
  selectedDpsIdAtom,
  selectedScenarioIdAtom,
  dpsScheduleResultAtom,
  dpsScheduleStatusAtom,
  scheduleDpsBaseAtom,
  fetchProcessStateBaseAtom,
} from './atoms';
import {
  useDpsSnapshotProgress,
  type SnapshotStatusItem,
} from './use-dps-snapshot-progress';

// ---------------------------------------------------------------------------
// Params & Return types
// ---------------------------------------------------------------------------

// Source key used to scope DPS-schedule notifications. Tagging the pushes
// lets a successful re-attempt clear stale errors from prior failures via
// `clearNotificationsBySource`, instead of leaving them piled up in the bar.
const DPS_NOTIFICATION_SOURCE = 'dps-schedule';

interface UseDpsSchedulingParams {
  packageId: number;
  selectedScenarioId: number | null;
  dcaScenarioId: number | null;
  dpsId: number | null;
  pushNotification: (msg: string, source?: string) => void;
  clearNotificationsBySource: (source: string) => void;
  setWorkingSlotDateConfigurations: (
    v: Record<string, SlotDateOverride>
  ) => void;
}

interface UseDpsSchedulingReturn {
  selectedDpsId: number | null;
  setSelectedDpsId: (id: number | null) => void;
  dpsScheduleResult: DpsScheduleResponse | null;
  setDpsScheduleResult: (
    v:
      | DpsScheduleResponse
      | null
      | ((prev: DpsScheduleResponse | null) => DpsScheduleResponse | null)
  ) => void;
  dpsScheduleStatus: DpsScheduleStatus;
  setDpsScheduleStatus: (v: DpsScheduleStatus) => void;
  /**
   * Schedules a DPS snapshot for `dpsId`. The optional `scenarioIdOverride`
   * lets callers that have *just* picked a scenario in the same synchronous
   * tick (e.g. the create-workspace modal) inject it directly, bypassing
   * the closure read of `selectedScenarioId`. Without an override, the
   * hook's currently-rendered `selectedScenarioId` is used (the existing
   * behavior, which is correct for the standalone DPS dropdown where the
   * picker has already settled into atom state).
   */
  handleDpsChange: (
    dpsId: number,
    scenarioIdOverride?: number | null
  ) => Promise<void>;
  // INS-8827: replaces handleEval2Change. When the user picks a different
  // scenario, the DPS snapshot must be rebuilt because the BE's slot
  // universe is filtered by the scenario's interest_type.
  handleScenarioChange: (scenarioId: number | null) => void;
  snapshotProgress: {
    statusItems: SnapshotStatusItem[];
    overallStatus: string;
    isBuilding: boolean;
    isComplete: boolean;
    isFailed: boolean;
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Encapsulates DPS scheduling state and side-effects: atom bindings for the
 * selected DPS ID, schedule result, and status; the Pusher subscription
 * lifecycle; the handleDpsChange / handleEval2Change callbacks; and the
 * per-step snapshot progress from the WorkflowProcess.
 *
 * @param {UseDpsSchedulingParams} params - Inputs the hook cannot own internally.
 * @return {UseDpsSchedulingReturn} Everything the component needs for DPS scheduling.
 */
export const useDpsScheduling = ({
  packageId,
  selectedScenarioId,
  dcaScenarioId,
  dpsId,
  pushNotification,
  clearNotificationsBySource,
  setWorkingSlotDateConfigurations,
}: UseDpsSchedulingParams): UseDpsSchedulingReturn => {
  const setSelectedScenarioId = useSetAtom(selectedScenarioIdAtom(packageId));
  // ---------------------------------------------------------------------------
  // DPS selection atoms
  // ---------------------------------------------------------------------------
  const [selectedDpsId, setSelectedDpsId] = useAtom(
    selectedDpsIdAtom(packageId)
  );
  const [dpsScheduleResult, setDpsScheduleResult] = useAtom(
    dpsScheduleResultAtom(packageId)
  );
  const [dpsScheduleStatus, setDpsScheduleStatus] = useAtom(
    dpsScheduleStatusAtom(packageId)
  );

  // ---------------------------------------------------------------------------
  // Mutation atom setters
  // ---------------------------------------------------------------------------
  const scheduleDps = useSetAtom(scheduleDpsBaseAtom);
  const fetchProcessState = useSetAtom(fetchProcessStateBaseAtom);

  // ---------------------------------------------------------------------------
  // Snapshot progress — channel + steps from schedule response
  // ---------------------------------------------------------------------------
  const [snapshotChannel, setSnapshotChannel] = useState<string | null>(null);
  const [snapshotSteps, setSnapshotSteps] = useState<
    DpsSnapshotStepEvent[] | null
  >(null);

  const snapshotProgress = useDpsSnapshotProgress({
    channel: snapshotChannel,
    steps: snapshotSteps,
  });

  // ---------------------------------------------------------------------------
  // Pusher subscription ref + cleanup
  // ---------------------------------------------------------------------------
  const dpsSubscriptionRef = useRef<{
    unbind: (event: string) => void;
    unsubscribe: () => void;
  } | null>(null);

  useEffect(() => {
    return () => {
      dpsSubscriptionRef.current?.unsubscribe();
    };
  }, []);

  // Mirrors dpsScheduleStatus into a ref so the post-subscribe recovery
  // path can read the latest value without re-creating the callback.
  const dpsScheduleStatusRef = useRef(dpsScheduleStatus);
  useEffect(() => {
    dpsScheduleStatusRef.current = dpsScheduleStatus;
  }, [dpsScheduleStatus]);

  // ---------------------------------------------------------------------------
  // bindDpsScheduleEvents — binds schedule_complete, slots_ready, and
  // schedule_error Pusher events to a subscription.
  // ---------------------------------------------------------------------------
  const bindDpsScheduleEvents = useCallback(
    (subscription: ReturnType<typeof subscribe>, errorLabel: string) => {
      dpsSubscriptionRef.current = subscription;

      subscription.bind(
        'schedule_complete',
        (data: DpsScheduleCompleteEvent) => {
          setDpsScheduleResult((prev) => ({
            slotsBlobPath: prev?.slotsBlobPath ?? '',
            scheduledSlotsBlobPath: data.scheduledSlotsBlobPath,
            scheduledSlotsSasUrl: data.scheduledSlotsSasUrl,
            slotCount: data.slotCount,
            errors: data.errors,
          }));
          setDpsScheduleStatus('caching');
        }
      );

      subscription.bind('slots_ready', (data: DpsSlotsReadyEvent) => {
        setDpsScheduleResult((prev) => ({
          ...(prev ?? {
            slotsBlobPath: '',
            scheduledSlotsBlobPath: '',
            slotCount: 0,
          }),
          slotsBlobPath: data.slotsBlobPath,
          scheduledSlotsBlobPath: data.scheduledSlotsBlobPath,
          slotCount: data.slotCount,
        }));
        setDpsScheduleStatus('ready');
        // Wipe any stale errors from prior failed schedule attempts now that
        // this one succeeded — otherwise the bar piles up old failures even
        // though the latest run worked.
        clearNotificationsBySource(DPS_NOTIFICATION_SOURCE);
        // Unbind only our events — do NOT call subscription.unsubscribe()
        // which would kill the shared channel and tear down the progress
        // hook's per-step bindings.
        subscription.unbind('slots_ready');
        subscription.unbind('schedule_error');
        subscription.unbind('schedule_complete');
        dpsSubscriptionRef.current = null;
      });

      subscription.bind('schedule_error', (data: { errors: string[] }) => {
        pushNotification(
          data.errors?.[0] ?? errorLabel,
          DPS_NOTIFICATION_SOURCE
        );
        setDpsScheduleStatus('error');
        subscription.unbind('slots_ready');
        subscription.unbind('schedule_error');
        subscription.unbind('schedule_complete');
        dpsSubscriptionRef.current = null;
      });
    },
    [
      setDpsScheduleResult,
      setDpsScheduleStatus,
      pushNotification,
      clearNotificationsBySource,
    ]
  );

  // ---------------------------------------------------------------------------
  // recoverScheduleFromState — closes the subscribe-after-fire race for fast
  // BE workflows. Empty configs (e.g. Royalty with no matching slots) finish
  // their SnapshotProcess in <1s; if the FE's Pusher subscribe lands after
  // `mark_as_completed!` has already emitted its terminal event, Pusher drops
  // the event and the dropdown spins forever.
  //
  // After binding, query the WorkflowProcess record once and reconstruct the
  // terminal event we missed, keyed on `overall_status`:
  //   - `processed` -> the schedule SUCCEEDED: mark 'ready' and clear stale
  //     errors, mirroring the `slots_ready` handler. Previously this path
  //     fabricated a `schedule_error`, turning a completed (often empty-Royalty)
  //     schedule into a false "Failed to schedule DPS" — the INS-11987 bug.
  //   - `failed`    -> a genuine failure whose `schedule_error` we missed:
  //     surface it so the loader stops.
  //   - pending/processing -> not terminal; Pusher will still deliver. Bail.
  // ---------------------------------------------------------------------------
  const recoverScheduleFromState = useCallback(
    async (
      subscription: ReturnType<typeof subscribe>,
      processId: number | undefined,
      errorLabel: string
    ) => {
      if (processId === undefined) return;

      let response;
      try {
        response = await fetchProcessState(processId);
      } catch {
        return;
      }
      const overallStatus = response.data?.overall_status;
      // Not terminal yet (pending/processing) — the terminal Pusher event will
      // still be delivered on the live channel, so leave it to the handlers.
      if (overallStatus !== 'processed' && overallStatus !== 'failed') return;

      // Pusher won the race (handler already moved status off 'scheduling'),
      // or a newer schedule call has superseded this subscription — bail.
      if (dpsSubscriptionRef.current !== subscription) return;
      if (dpsScheduleStatusRef.current !== 'scheduling') return;

      if (overallStatus === 'processed') {
        // The backend already SUCCEEDED but we missed its terminal Pusher event:
        // the SnapshotProcess finished before our subscribe landed (fast Royalty/
        // empty-slot configs complete in <1s). Recover the success instead of
        // fabricating a failure — mark ready and clear any stale error toast,
        // mirroring the `slots_ready` handler. An empty snapshot recovers to a
        // ready-but-not-runnable state (isValidConfigAtom still gates on the blob
        // paths), exactly as a page reload would surface it. (INS-11987)
        setDpsScheduleStatus('ready');
        clearNotificationsBySource(DPS_NOTIFICATION_SOURCE);
      } else {
        // Genuinely failed (terminal, not 'processed') and we missed the
        // `schedule_error` event — surface the failure so the loader stops.
        pushNotification(errorLabel, DPS_NOTIFICATION_SOURCE);
        setDpsScheduleStatus('error');
      }
      subscription.unbind('slots_ready');
      subscription.unbind('schedule_error');
      subscription.unbind('schedule_complete');
      dpsSubscriptionRef.current = null;
    },
    [
      fetchProcessState,
      pushNotification,
      setDpsScheduleStatus,
      clearNotificationsBySource,
    ]
  );

  // ---------------------------------------------------------------------------
  // handleDpsChange — selects a DPS, enqueues scheduling, and subscribes to
  // Pusher for two-phase completion (schedule_complete, slots_ready).
  // ---------------------------------------------------------------------------
  const handleDpsChange = useCallback(
    async (newDpsId: number, scenarioIdOverride?: number | null) => {
      dpsSubscriptionRef.current?.unsubscribe();
      dpsSubscriptionRef.current = null;

      setSelectedDpsId(newDpsId);
      setDpsScheduleResult(null);
      setWorkingSlotDateConfigurations({});
      setDpsScheduleStatus('scheduling');
      setSnapshotChannel(null);
      setSnapshotSteps(null);

      // Prefer the caller-supplied scenarioId when present so a same-tick
      // pairing of `setSelectedScenarioId(...) → handleDpsChange(...)` (the
      // create-workspace modal flow) doesn't read a stale closure value
      // and schedule the snapshot against the wrong slot universe. When no
      // override is supplied (e.g. the standalone DPS dropdown), fall back
      // to the prop — the picker state has already settled by then.
      const effectiveScenarioId =
        scenarioIdOverride !== undefined
          ? scenarioIdOverride
          : selectedScenarioId;

      try {
        // BE derives interest_type from `scenario_id` server-side
        // (INS-8827); the FE no longer needs to look it up.
        const response = await scheduleDps(
          packageId,
          newDpsId,
          effectiveScenarioId,
          dcaScenarioId
        );

        // Store channel + steps for snapshot progress tracking
        setSnapshotChannel(response.channel);
        if (response.steps) setSnapshotSteps(response.steps);

        const subscription = subscribe(response.channel);
        bindDpsScheduleEvents(subscription, 'Failed to schedule DPS');
        recoverScheduleFromState(
          subscription,
          response.process_id,
          'Failed to schedule DPS'
        );
      } catch (err) {
        pushNotification(
          err instanceof Error ? err.message : 'Failed to schedule DPS',
          DPS_NOTIFICATION_SOURCE
        );
        setDpsScheduleStatus('error');
      }
    },
    [
      packageId,
      selectedScenarioId,
      dcaScenarioId,
      setSelectedDpsId,
      setDpsScheduleResult,
      setWorkingSlotDateConfigurations,
      setDpsScheduleStatus,
      scheduleDps,
      pushNotification,
      bindDpsScheduleEvents,
      recoverScheduleFromState,
    ]
  );

  // ---------------------------------------------------------------------------
  // handleScenarioChange — when the Scenario picker changes and a DPS is
  // active, the interest_type derived server-side from the new scenario may
  // differ, so the slot universe must be re-snapshotted. Mirrors the legacy
  // handleEval2Change behavior under the INS-8827 contract.
  // ---------------------------------------------------------------------------
  const handleScenarioChange = useCallback(
    (scenarioId: number | null) => {
      setSelectedScenarioId(scenarioId);

      const activeDpsId = dpsId;
      if (!activeDpsId || scenarioId === null) return;

      dpsSubscriptionRef.current?.unsubscribe();
      dpsSubscriptionRef.current = null;
      setDpsScheduleResult(null);
      setWorkingSlotDateConfigurations({});
      setDpsScheduleStatus('scheduling');
      setSnapshotChannel(null);
      setSnapshotSteps(null);

      scheduleDps(packageId, activeDpsId, scenarioId, dcaScenarioId)
        .then((response) => {
          setSnapshotChannel(response.channel);
          if (response.steps) setSnapshotSteps(response.steps);

          const subscription = subscribe(response.channel);
          bindDpsScheduleEvents(subscription, 'Failed to reschedule DPS');
          recoverScheduleFromState(
            subscription,
            response.process_id,
            'Failed to reschedule DPS'
          );
        })
        .catch((err) => {
          pushNotification(
            err instanceof Error ? err.message : 'Failed to reschedule DPS',
            DPS_NOTIFICATION_SOURCE
          );
          setDpsScheduleStatus('error');
        });
    },
    [
      packageId,
      dpsId,
      dcaScenarioId,
      setSelectedScenarioId,
      setDpsScheduleResult,
      setWorkingSlotDateConfigurations,
      setDpsScheduleStatus,
      scheduleDps,
      pushNotification,
      bindDpsScheduleEvents,
      recoverScheduleFromState,
    ]
  );

  return {
    selectedDpsId,
    setSelectedDpsId,
    dpsScheduleResult,
    setDpsScheduleResult,
    dpsScheduleStatus,
    setDpsScheduleStatus,
    handleDpsChange,
    handleScenarioChange,
    snapshotProgress,
  };
};
